<script setup>
// SiteUp: bolha das respostas do formulário de lead (Meta Lead Ads e captura
// própria). Irmã do AdMessage: mesmo desenho de card, porque para o atendente
// as duas coisas são a mesma cena (o lead chegou por anúncio e já respondeu
// algo antes de falar).
//
// Antes disso as respostas não apareciam em lugar nenhum: a nota era opt-in por
// endpoint e estava desligada na Plus Energy — zero notas em toda a base. O
// atendente pegava o lead pago sem saber o que a pessoa tinha respondido.
//
// O texto continua no content da mensagem (busca e fallback seguem valendo);
// aqui só desenhamos o que veio estruturado em content_attributes.leadForm.
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const { contentAttributes } = useMessageContext();

const leadForm = computed(() => contentAttributes?.value?.leadForm || {});
const fields = computed(() =>
  (leadForm.value.fields || []).filter(field => field?.label && field?.value)
);
const creativeUrl = computed(() => leadForm.value.creativeUrl || '');
const formName = computed(() => leadForm.value.formName || '');

// Duas respostas já dizem de onde o lead vem; o resto abre no clique para o
// card não virar a parede de texto que ele veio substituir.
const PREVIEW = 2;
const expanded = ref(false);
const visibleFields = computed(() =>
  expanded.value ? fields.value : fields.value.slice(0, PREVIEW)
);
const hiddenCount = computed(() => Math.max(fields.value.length - PREVIEW, 0));
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="lead-form-message">
    <div class="overflow-hidden rounded-xl bg-n-alpha-2">
      <img
        v-if="creativeUrl"
        :src="creativeUrl"
        class="object-cover w-full max-h-40"
        :alt="$t('CONVERSATION.LEAD_FORM.CREATIVE_ALT')"
      />
      <div class="flex gap-2.5 items-center p-2.5">
        <span
          class="flex justify-center items-center rounded-[var(--workspace-radius,10px)] size-7 shrink-0 bg-white/[0.06]"
        >
          <Icon class="size-3.5 text-n-teal-9" icon="i-ph-list-checks" />
        </span>
        <span class="flex flex-col flex-1 min-w-0">
          <span
            class="text-[13px] font-bold leading-tight tracking-tight text-n-slate-12"
          >
            {{ $t('CONVERSATION.LEAD_FORM.TITLE') }}
          </span>
          <span
            v-if="formName"
            class="mt-0.5 text-xs leading-snug truncate text-n-slate-11"
          >
            {{ formName }}
          </span>
        </span>
      </div>

      <dl
        v-if="visibleFields.length"
        data-testid="lead-form-fields"
        class="flex flex-col gap-1.5 px-2.5 pb-2.5"
      >
        <div
          v-for="field in visibleFields"
          :key="field.label"
          class="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-2.5 items-baseline text-xs"
        >
          <dt class="truncate text-n-slate-11" :title="field.label">
            {{ field.label }}
          </dt>
          <dd class="m-0 font-semibold break-words text-n-slate-12">
            {{ field.value }}
          </dd>
        </div>
      </dl>

      <button
        v-if="hiddenCount"
        type="button"
        data-testid="lead-form-toggle"
        class="flex gap-1 justify-center items-center px-2.5 pb-2.5 w-full text-xs font-semibold transition-colors text-n-slate-11 hover:text-n-slate-12"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        {{
          expanded
            ? $t('CONVERSATION.LEAD_FORM.SHOW_LESS')
            : $t('CONVERSATION.LEAD_FORM.SHOW_MORE', { count: hiddenCount })
        }}
        <Icon
          class="transition-transform duration-150 size-3.5"
          :class="{ 'rotate-180': expanded }"
          icon="i-ph-caret-down"
        />
      </button>
    </div>
  </BaseBubble>
</template>
